/**
 * Global Error Interceptor
 *
 * Catches errors that escape React's error boundary:
 *  - window.onerror (synchronous throws outside React tree)
 *  - unhandledrejection (async errors, failed fetches, tRPC timeouts)
 *  - offline/online transitions (network connectivity)
 *
 * Surfaces user-facing toasts via Sonner so failures are never silent.
 */
import { toast } from "sonner";

let installed = false;

export function installGlobalErrorInterceptor() {
  if (installed) return;
  installed = true;

  // ── Unhandled promise rejections ────────────────────────────────────────
  window.addEventListener("unhandledrejection", (event) => {
    const reason = event.reason;
    const msg =
      reason instanceof Error ? reason.message : String(reason ?? "Unknown async error");

    // Don't toast for auth redirects (handled by main.tsx)
    if (msg.includes("10001") || msg.includes("Please login")) return;

    console.error("[Global] Unhandled rejection:", reason);
    toast.error("Async operation failed", {
      description: msg.slice(0, 150),
      duration: 6000,
    });
  });

  // ── Synchronous errors outside React tree ───────────────────────────────
  window.addEventListener("error", (event) => {
    // Ignore ResizeObserver loop errors (benign, browser-internal)
    if (event.message?.includes("ResizeObserver")) return;

    console.error("[Global] Uncaught error:", event.error ?? event.message);
    toast.error("Unexpected error", {
      description: (event.error?.message ?? event.message ?? "").slice(0, 150),
      duration: 6000,
    });
  });

  // ── Network connectivity ────────────────────────────────────────────────
  window.addEventListener("offline", () => {
    toast.warning("You're offline", {
      description: "Some features may not work until your connection is restored.",
      duration: Infinity,
      id: "network-status",
    });
  });

  window.addEventListener("online", () => {
    toast.success("Back online", {
      description: "Connection restored.",
      duration: 3000,
      id: "network-status",
    });
  });
}
